import { companiesApi } from '@/lib/api';
import { Button, Icon } from '@pms-digital/design-system';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const RESTORE_NOTICE_ERROR_MESSAGE = 'La restauration a échoué. Réessayez.';

export interface ArchivedCompanyNoticeProps {
  companyId: string;
  /** « Restaurer » is offered to pms_user and pms_admin only; the others are pointed to the PMS team. */
  canRestore: boolean;
  /**
   * `page` (default) stands in for a whole page. `banner` sits above a page that
   * stays on screen read-only (the wizard and the export page, issue #22): same
   * title, same « Restaurer », in a bar instead of a centred card.
   */
  layout?: 'page' | 'banner';
}

/**
 * Stands in for the whole page of an archived company and of its matrices
 * (issue #20, planche 05): no title, tabs or matrices — the company is closed.
 * A restore needs no confirmation; it refreshes the company, which brings the
 * normal page back on its own.
 */
export function ArchivedCompanyNotice({
  companyId,
  canRestore,
  layout = 'page',
}: ArchivedCompanyNoticeProps) {
  const queryClient = useQueryClient();
  const restoreMutation = useMutation({
    mutationFn: () => companiesApi.restore(companyId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      void queryClient.invalidateQueries({ queryKey: ['matrix'] });
    },
  });

  if (layout === 'banner') {
    return (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border-2 border-gray-100 bg-gray-50 px-4 py-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">
          <Icon name="archive" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-bold text-body text-noir">Cette entreprise est archivée</h2>
          <p className="text-gray-700 text-small">
            Cette page est en lecture seule : les modifications sont désactivées.
            {canRestore ? '' : " Contactez l'équipe PMS pour la restaurer."}
          </p>
        </div>
        {canRestore && (
          <div className="flex flex-col items-end gap-1">
            <Button
              variant="secondary"
              type="button"
              disabled={restoreMutation.isPending}
              onClick={() => restoreMutation.mutate()}
            >
              <Icon name="unarchive" size={16} />
              {restoreMutation.isPending ? 'Restauration…' : 'Restaurer'}
            </Button>
            {restoreMutation.isError && (
              <p role="alert" className="font-semibold text-rouge-deep text-small">
                {RESTORE_NOTICE_ERROR_MESSAGE}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto mt-12 flex max-w-[480px] flex-col items-center gap-2 rounded-lg border-2 border-gray-100 bg-gray-50 px-8 py-8 text-center">
      <span className="mb-1 flex size-14 items-center justify-center rounded-full bg-gray-100 text-gray-600">
        <Icon name="archive" size={24} />
      </span>
      <h2 className="font-bold text-h4 text-noir">Cette entreprise est archivée</h2>
      <p className="text-gray-700 text-small">
        Ses matrices et son setup ne sont plus accessibles.
      </p>
      {canRestore ? (
        <>
          <Button
            variant="secondary"
            type="button"
            disabled={restoreMutation.isPending}
            onClick={() => restoreMutation.mutate()}
          >
            <Icon name="unarchive" size={16} />
            {restoreMutation.isPending ? 'Restauration…' : 'Restaurer'}
          </Button>
          {restoreMutation.isError && (
            <p role="alert" className="font-semibold text-rouge-deep text-small">
              {RESTORE_NOTICE_ERROR_MESSAGE}
            </p>
          )}
        </>
      ) : (
        <p className="text-gray-700 text-small">Contactez l'équipe PMS pour la restaurer.</p>
      )}
    </div>
  );
}
